import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  Switch,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Menu,
  Fade,
  Slide,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveIcon from "@mui/icons-material/Save";
import { AddRounded } from "@mui/icons-material";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import ColorLensRoundedIcon from "@mui/icons-material/ColorLensRounded";
import FormatColorFillRoundedIcon from "@mui/icons-material/FormatColorFillRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import { pageScrollStyle } from "../../../../../styles/styles";
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { fieldTypeLabelMap } from "../../../../../static/staticdata";

const DefaultColors = [
  "#F87171",
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#A78BFA",
  "#F97316",
];

const EditProperties = ({
  setSections,
  sectionId,
  fieldId,
  field,
  handleUpdateFieldLabel,
  handleUpdateFieldProperty,
  setInputFieldMenuOpen,
}) => {
  console.log(field);

  // Properly handle the case where pickList might be undefined or have different structures
  const hasLinkOption =
    field.pickList?.link_option &&
    (typeof field.pickList.link_option === "string" ||
      field.pickList.link_option.tab);

  const linkOptionValue = hasLinkOption
    ? typeof field.pickList.link_option === "string"
      ? field.pickList.link_option
      : field.pickList.link_option.tab
    : "";

  // Default options if neither options nor link_option exists
  const defaultOptions = [
    { label: "Option 1", value: "option_1" },
    { label: "Option 2", value: "option_2" },
  ];

  // Initialize options based on what's available in field.pickList
  const initialOptions =
    field.pickList?.options || (!hasLinkOption ? defaultOptions : []);

  const [pickListOptionCount, setPickListOptionCount] = useState(
    initialOptions.length || 2
  );
  const [colorMenuOpen, setColorMenuOpen] = useState(null);
  const [activeOptionIndex, setActiveOptionIndex] = useState(null);
  const [optionColors, setOptionColors] = useState(
    initialOptions.map(
      (opt, i) => opt.color || DefaultColors[i % DefaultColors.length]
    )
  );
  const [customColor, setCustomColor] = useState("#34D399");
  const [isFullscreenView, setIsFullscreenView] = useState(false);
  const [options, setOptions] = useState(initialOptions);

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      label: field.label ?? "",
      required: field.required ?? false,
      tooltip: field.tooltip ?? false,
      tooltipText: field.tooltipText ?? "",
      pickList: {
        link_option: linkOptionValue,
        options: initialOptions,
        multiSelect: field.pickList?.multiSelect ?? false,
        useColors: field.pickList?.useColors ?? false,
        defaultValue:
          field.pickList?.defaultValue ??
          (initialOptions.length > 0 ? initialOptions[0]?.value : "none"),
      },
    },
  });

  useEffect(() => {
    if (
      field.value === "pick_list" &&
      field.pickList &&
      !field.pickList.defaultValue &&
      initialOptions.length > 0
    ) {
      setValue("pickList.defaultValue", initialOptions[0].value);
    }
  }, [field.value, field.pickList, initialOptions, setValue]);

  const showTooltip = watch("tooltip");
  // Make sure we're watching the correct path to the link_option
  const link_option = watch("pickList.link_option");

  const openColorMenu = (event, index) => {
    setActiveOptionIndex(index);
    setColorMenuOpen(event.currentTarget);
    setCustomColor(optionColors[index] || "#34D399");
  };

  const handleColorChange = (color) => {
    const newOptionColors = [...optionColors];
    newOptionColors[activeOptionIndex] = color;
    setOptionColors(newOptionColors);

    setValue(`pickList.options[${activeOptionIndex}].color`, color);

    setColorMenuOpen(null);
  };

  const handleCustomColorChange = (e) => {
    setCustomColor(e.target.value);
  };

  // Updated onSubmit function that properly handles nested properties
  const onSubmit = (data) => {
    // Create a new pickList object to avoid modifying the original
    const updatedPickList = { ...data.pickList };

    // Check if link_option is selected
    if (updatedPickList.link_option && updatedPickList.link_option !== "") {
      // Format link_option as an object if it's a string
      updatedPickList.link_option = {
        tab: updatedPickList.link_option,
        tab_api:
          updatedPickList.link_option === "contact"
            ? "https://api.lexmom.com/api/v1/contact/retrieve/"
            : updatedPickList.link_option === "matter"
            ? "https://api.lexmom.com/api/v1/case/register/"
            : updatedPickList.link_option === "member"
            ? "https://api.lexmom.com/api/v1/team/create-employee/"
            : null,
      };

      // Remove options if link_option is present
      delete updatedPickList.options;
    } else if (updatedPickList.options) {
      // Process options if link_option is not selected
      updatedPickList.options = updatedPickList.options.map(
        (option, index) => ({
          ...option,
          color:
            optionColors[index] || DefaultColors[index % DefaultColors.length],
          value: option.value || `option_${index + 1}`,
          label: option.label || option.value || `Option ${index + 1}`,
        })
      );

      if (!updatedPickList.defaultValue && updatedPickList.options.length > 0) {
        updatedPickList.defaultValue = updatedPickList.options[0].value;
      }

      // Ensure link_option is removed completely
      delete updatedPickList.link_option;
    }

    // Update field label if provided
    if (data.label) {
      handleUpdateFieldLabel(sectionId, fieldId, data.label);
    }

    // Update pickList property
    handleUpdateFieldProperty(sectionId, fieldId, "pickList", updatedPickList);

    // Update other properties
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "label" && key !== "pickList") {
        handleUpdateFieldProperty(sectionId, fieldId, key, value);
      }
    });

    setInputFieldMenuOpen(null);
  };

  // Update options state when option count changes
  useEffect(() => {
    // Only update options if link_option is not selected
    if (link_option && link_option !== "") return;

    const currentOptions = watch("pickList.options") || [];

    // Add new options if needed
    if (currentOptions.length < pickListOptionCount) {
      const newOptions = [...currentOptions];
      for (let i = currentOptions.length; i < pickListOptionCount; i++) {
        newOptions.push({
          label: `Option ${i + 1}`,
          value: `option_${i + 1}`,
          color: DefaultColors[i % DefaultColors.length],
        });
      }
      setValue("pickList.options", newOptions);
      setOptions(newOptions);
    }
    // Remove options if needed
    else if (currentOptions.length > pickListOptionCount) {
      const newOptions = currentOptions.slice(0, pickListOptionCount);
      setValue("pickList.options", newOptions);
      setOptions(newOptions);
    }
  }, [pickListOptionCount, setValue, watch, link_option]);

  // Update form when link_option changes
  useEffect(() => {
    if (link_option && link_option !== "") {
      // If link_option is selected, clear options
      setValue("pickList.options", []);
      setOptions([]);
    } else if (options.length === 0) {
      // If link_option is deselected and no options exist, set default options
      const defaultOpts = [
        { label: "Option 1", value: "option_1", color: DefaultColors[0] },
        { label: "Option 2", value: "option_2", color: DefaultColors[1] },
      ];
      setValue("pickList.options", defaultOpts);
      setOptions(defaultOpts);
      setPickListOptionCount(2);
    }
  }, [link_option, setValue, options.length]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "primary.main",
          px: "0.5rem",
          py: "0.2rem",
        }}
      >
        <Typography
          variant="body1"
          sx={{ color: "#fff", fontSize: "0.9rem", fontWeight: 400 }}
        >
          Field Properties
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Don't save & close">
            <IconButton
              size="small"
              color="error"
              onClick={() => setInputFieldMenuOpen(null)}
            >
              <CloseRoundedIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="save & close">
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={handleSubmit(onSubmit)}
            >
              <SaveIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          p: "0.5rem",
          maxHeight: "15rem",
          overflowY: "auto",
          ...pageScrollStyle,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            justifyContent: "start",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: "0.8rem", fontWeight: "500" }}
          >
            Field Label:
          </Typography>
          <Controller
            name="label"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "0.2rem",
                  padding: "0.2rem",
                  fontSize: "0.8rem",
                  flex: 1,
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Typography
            variant="body1"
            sx={{ fontSize: "0.8rem", fontWeight: "500", whiteSpace: "nowrap" }}
          >
            Field Type:
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              gap: "1rem",
              width: "100%",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.8rem",
                fontWeight: "500",
                color: "text.secondary",
              }}
            >
              {fieldTypeLabelMap[field?.value] || "Unknown Field Type"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Typography
            variant="body1"
            sx={{ fontSize: "0.8rem", fontWeight: "500", whiteSpace: "nowrap" }}
          >
            Required:
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            <Controller
              name="required"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  checked={value}
                  onChange={onChange}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#00B3B0" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#00B3B0",
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.8rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
              }}
            >
              Show Tooltip:
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
              }}
            >
              <Controller
                name="tooltip"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    checked={value}
                    onChange={onChange}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#00B3B0",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#00B3B0",
                        },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          {showTooltip && (
            <Controller
              name="tooltipText"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  style={{
                    width: "100%",
                    border: "1px solid #e0e0e0",
                    borderRadius: "0.2rem",
                    padding: "0.2rem",
                    fontSize: "0.8rem",
                    marginTop: "0.5rem",
                  }}
                  placeholder="Enter tooltip text..."
                />
              )}
            />
          )}
        </Box>

        {field.value === "single_line" && (
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "0.8rem", fontWeight: "500" }}
            >
              Character Limit:
            </Typography>
            <Controller
              name="characterLimit"
              control={control}
              defaultValue={255}
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "0.2rem",
                    padding: "0.2rem",
                    fontSize: "0.8rem",
                    flex: 1,
                  }}
                />
              )}
            />
          </Box>
        )}
        {field.value === "phone" && (
          <Box
            sx={{
              display: "flex",
              gap: "1rem",
              justifyContent: "start",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "0.8rem", fontWeight: "500" }}
            >
              Character Limit:
            </Typography>
            <Controller
              name="phoneNoCharacterLimit"
              control={control}
              defaultValue={30}
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "0.2rem",
                    padding: "0.2rem",
                    fontSize: "0.8rem",
                    flex: 1,
                  }}
                />
              )}
            />
          </Box>
        )}
        {field.value === "pick_list" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              justifyContent: "center",
              alignItems: "start",
              borderRadius: "0.3rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                Multi Select:
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="pickList.multiSelect"
                  control={control}
                  defaultValue={field.pickList?.multiSelect || false}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#00B3B0",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#00B3B0",
                          },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                Color for options:
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="pickList.useColors"
                  control={control}
                  defaultValue={field.pickList?.useColors || false}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#00B3B0",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#00B3B0",
                          },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                Default value:
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="pickList.defaultValue"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "0.2rem",
                        padding: "0.2rem",
                        fontSize: "0.8rem",
                        flex: 1,
                      }}
                    >
                      <option value="none">None</option>
                      {(!link_option || link_option === "") &&
                        options.map((option, i) => (
                          <option key={i} value={option.value}>
                            {option.label || option.value}
                          </option>
                        ))}
                    </select>
                  )}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                Link Option:
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                <Controller
                  name="pickList.link_option"
                  control={control}
                  render={({ field }) => (
                    <select
                      {...field}
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "0.2rem",
                        padding: "0.2rem",
                        fontSize: "0.8rem",
                        flex: 1,
                      }}
                    >
                      <option value="">None</option>
                      <option value="contact">Contact</option>
                      <option value="matter">Matter</option>
                      <option value="member">Member</option>
                    </select>
                  )}
                />
              </Box>
            </Box>
            {(!link_option || link_option === "") && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  justifyContent: "center",
                  alignItems: "start",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "0.8rem", fontWeight: "500" }}
                  >
                    Options:
                  </Typography>
                  <Tooltip title="Big view">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setIsFullscreenView(true)}
                    >
                      <FullscreenRoundedIcon sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {[...Array(pickListOptionCount)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "0.3rem",
                      width: "100%",
                    }}
                  >
                    {watch("pickList.useColors") && (
                      <Box
                        onClick={(e) => openColorMenu(e, i)}
                        sx={{
                          width: "1rem",
                          height: "1rem",
                          borderRadius: "50%",
                          cursor: "pointer",
                          bgcolor:
                            optionColors[i] ||
                            DefaultColors[i % DefaultColors.length],
                          border: "1px solid #fff",
                          outline: `1px solid ${
                            optionColors[i] ||
                            DefaultColors[i % DefaultColors.length]
                          }`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    )}

                    <Controller
                      name={`pickList.options[${i}].value`}
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "0.2rem",
                            padding: "0.2rem",
                            fontSize: "0.8rem",
                            flex: 1,
                          }}
                        />
                      )}
                    />

                    {/* Hidden field to store color value */}
                    <Controller
                      name={`pickList.options[${i}].color`}
                      control={control}
                      defaultValue={
                        optionColors[i] ||
                        DefaultColors[i % DefaultColors.length]
                      }
                      render={({ field }) => <input type="hidden" {...field} />}
                    />

                    <Tooltip title="Delete Option">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (pickListOptionCount > 2) {
                            setPickListOptionCount(pickListOptionCount - 1);
                          }
                        }}
                      >
                        <RemoveRoundedIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Option">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          setPickListOptionCount(pickListOptionCount + 1)
                        }
                      >
                        <AddRounded sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ---------------------------------- MENU OF OPTIONS COLOR PICKER ---------------------------------- */}
      <Menu
        anchorEl={colorMenuOpen}
        open={Boolean(colorMenuOpen)}
        onClose={() => setColorMenuOpen(null)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "hidden",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              "& .MuiList-root": {
                p: 0,
              },
            },
          },
        }}
        transformOrigin={{
          horizontal: "left",
          vertical: "center",
        }}
        anchorOrigin={{ horizontal: "right", vertical: "center" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.5rem",
            p: "0.3rem",
          }}
        >
          {DefaultColors.map((color, i) => (
            <Slide
              key={i}
              direction="up"
              in={Boolean(colorMenuOpen)}
              timeout={200 + i * 50}
            >
              <Box
                onClick={() => handleColorChange(color)}
                sx={{
                  height: "1rem",
                  width: "1rem",
                  borderRadius: "50%",
                  bgcolor: color,
                  cursor: "pointer",
                  border:
                    color === optionColors[activeOptionIndex]
                      ? "1px solid black"
                      : "none",
                  transform: "translateY(10px)",
                  transition: "transform 0.3s ease-in-out",
                  "&:hover": {
                    transform: "translateY(0)",
                  },
                }}
              />
            </Slide>
          ))}

          {/* Custom Color Picker */}
          <Slide direction="up" in={Boolean(colorMenuOpen)} timeout={500}>
            <IconButton
              component="label"
              size="small"
              onClick={() => handleColorChange(customColor)}
            >
              <FormatColorFillRoundedIcon sx={{ fontSize: "1rem" }} />

              <input
                type="color"
                value={customColor}
                onChange={handleCustomColorChange}
                style={{
                  position: "absolute",
                  opacity: 0,
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
              />
            </IconButton>
          </Slide>
        </Box>
      </Menu>
      {/* --------------------------------------------- BIG VIEW OF THE OPTION MENU ------------------------------------------- */}
      <Dialog
        open={isFullscreenView}
        onClose={() => setIsFullscreenView(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            ...pageScrollStyle,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "primary.main",
            px: "1rem",
            py: "0.5rem",
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: "#fff", fontSize: "1rem", fontWeight: 500 }}
          >
            Pick List Options
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <Tooltip title="Color for options">
              <Box display="inline-block">
                <Controller
                  name="pickList.useColors"
                  control={control}
                  defaultValue={field.pickList?.useColors || false}
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#004242",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#004242",
                          },
                      }}
                    />
                  )}
                />
              </Box>
            </Tooltip>
            <Tooltip title="Add option">
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => setPickListOptionCount(pickListOptionCount + 1)}
              >
                <AddRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title="small view">
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => setIsFullscreenView(false)}
              >
                <FullscreenExitRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <DialogContent sx={{ p: "0" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
              maxHeight: "20rem",
              overflow: "auto",
              ...pageScrollStyle,
            }}
          >
            {[...Array(pickListOptionCount)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "1rem",
                  width: "100%",
                }}
              >
                {watch("pickList.useColors") && (
                  <Box
                    onClick={(e) => openColorMenu(e, i)}
                    sx={{
                      width: "2rem",
                      height: "1.5rem",
                      borderRadius: "50%",
                      cursor: "pointer",
                      bgcolor:
                        optionColors[i] ||
                        DefaultColors[i % DefaultColors.length],
                      border: "1px solid #fff",
                      outline: `1px solid ${
                        optionColors[i] ||
                        DefaultColors[i % DefaultColors.length]
                      }`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ColorLensRoundedIcon
                      sx={{ fontSize: "1rem", color: "#fff" }}
                    />
                  </Box>
                )}

                <Controller
                  name={`pickList.options[${i}].value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant="outlined"
                      size="small"
                      label={`Option ${i + 1}`}
                    />
                  )}
                />

                {/* Hidden field to store color value */}
                <Controller
                  name={`pickList.options[${i}].color`}
                  control={control}
                  defaultValue={
                    optionColors[i] || DefaultColors[i % DefaultColors.length]
                  }
                  render={({ field }) => <input type="hidden" {...field} />}
                />

                <Box sx={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      if (pickListOptionCount > 2) {
                        setPickListOptionCount(pickListOptionCount - 1);
                      }
                    }}
                    startIcon={<CloseRoundedIcon />}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EditProperties;
